import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {memo, useCallback, useMemo} from 'react'

import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import type {PageTypeForGroupedItems} from '../../state-providers/memex-items/queries/query-keys'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {TextPlaceholder} from '../common/placeholders'
import {useVisiblePagination} from '../common/use-visible-pagination'
import {CardBaseWithSash} from './card/card-base-with-sash'
import {ColumnFrame} from './column/column-frame'
import {COLUMN_STYLE} from './constants'

export const PLACEHOLDER_CARD_COUNT = 5

export const BoardPagination: React.FC<{groupId?: string; secondaryGroupId?: string}> = ({
  groupId,
  secondaryGroupId,
}) => {
  const {memex_mwl_swimlanes} = useEnabledFeatures()

  if (memex_mwl_swimlanes && groupId && secondaryGroupId) {
    return <SwimlaneCellPagination groupId={groupId} secondaryGroupId={secondaryGroupId} />
  } else if (groupId) {
    return <PaginationWithCardPlaceholder groupId={groupId} />
  } else {
    return <PaginationWithColumnPlaceholder />
  }
}

const PaginationWithColumnPlaceholder: React.FC = () => {
  const {ref, hasNextPage} = useVisiblePagination({groupId: undefined})
  return (
    <div ref={ref} {...testIdProps('board-pagination')}>
      {hasNextPage ? <PlaceholderColumn /> : null}
    </div>
  )
}

const PaginationWithCardPlaceholder: React.FC<{groupId: string}> = ({groupId}) => {
  const {ref, hasNextPage} = useVisiblePagination({groupId})
  return (
    <div ref={ref} {...testIdProps(`board-pagination-${groupId}`)}>
      {hasNextPage ? <PlaceholderCard /> : null}
    </div>
  )
}

const SwimlaneCellPagination: React.FC<{groupId: string; secondaryGroupId: string}> = ({groupId, secondaryGroupId}) => {
  const {hasNextPageForGroupedItems, isFetchingNextPageForGroupedItems, fetchNextPageForGroupedItems} =
    usePaginatedMemexItemsQuery()

  const pageType: PageTypeForGroupedItems = useMemo(() => ({groupId, secondaryGroupId}), [groupId, secondaryGroupId])
  const hasNextPage = hasNextPageForGroupedItems(pageType)
  const isFetchingNextPage = isFetchingNextPageForGroupedItems(pageType)

  const loadMoreItemsOnClick = useCallback(() => {
    fetchNextPageForGroupedItems(pageType)
  }, [fetchNextPageForGroupedItems, pageType])

  return (
    <div {...testIdProps(`board-pagination-${groupId}-${secondaryGroupId}`)}>
      {isFetchingNextPage ? <PlaceholderCard /> : null}
      {hasNextPage ? (
        <Button onClick={loadMoreItemsOnClick} disabled={isFetchingNextPage}>
          Load more items
        </Button>
      ) : null}
    </div>
  )
}

const PlaceholderCardUnmemoized: React.FC = () => {
  return (
    <CardBaseWithSash
      {...testIdProps('placeholder-card')}
      className="board-view-column-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        backgroundColor: 'canvas.overlay',
        borderColor: 'border.default',
        borderStyle: 'solid',
        borderRadius: 2,
        borderWidth: '1px',
      }}
    >
      {[...Array(PLACEHOLDER_CARD_COUNT).keys()].map(key => (
        <TextPlaceholder style={{height: 8}} key={key} minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
      ))}
    </CardBaseWithSash>
  )
}

const PlaceholderColumnUnmemoized: React.FC = () => {
  return (
    <ColumnFrame
      sx={{
        ...COLUMN_STYLE,
        p: 2,
        gap: 2,
      }}
      className="visible"
      headerContent={
        <TextPlaceholder
          style={{height: 12}}
          minWidth={80}
          maxWidth={200}
          {...testIdProps('placeholder-column-header')}
        />
      }
    >
      <PlaceholderCard />
    </ColumnFrame>
  )
}

const PlaceholderCard = memo(PlaceholderCardUnmemoized)
const PlaceholderColumn = memo(PlaceholderColumnUnmemoized)

try{ BoardPagination.displayName ||= 'BoardPagination' } catch {}
try{ PaginationWithColumnPlaceholder.displayName ||= 'PaginationWithColumnPlaceholder' } catch {}
try{ PaginationWithCardPlaceholder.displayName ||= 'PaginationWithCardPlaceholder' } catch {}
try{ SwimlaneCellPagination.displayName ||= 'SwimlaneCellPagination' } catch {}
try{ PlaceholderCardUnmemoized.displayName ||= 'PlaceholderCardUnmemoized' } catch {}
try{ PlaceholderColumnUnmemoized.displayName ||= 'PlaceholderColumnUnmemoized' } catch {}
try{ PlaceholderCard.displayName ||= 'PlaceholderCard' } catch {}
try{ PlaceholderColumn.displayName ||= 'PlaceholderColumn' } catch {}